// src/features/settings/api/useSubscriptions.ts
// Subscription tiers, organization subscription, and add-on modules
// All endpoints verified against openapi.json:
//   billing-service:       GET /api/subscriptions/tiers, GET /api/subscriptions/tiers/{id}
//   subscriptions-service: GET/PUT /api/subscriptions/organization
//                          GET /api/subscriptions/modules/available
//                          GET /api/subscriptions/modules
//                          POST /api/subscriptions/modules/{module_id}
//                          DELETE /api/subscriptions/modules/{module_id}
//                          PUT /api/subscriptions/modules/{module_id}/toggle
// © 2025–2026 MCX Services, LLC. All rights reserved.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'

// ============================================================================
// Types — verified against openapi.json schemas
// ============================================================================

/** billing_service_SubscriptionResponse */
export interface SubscriptionTier {
    id: number
    tier: string
    status: string
    max_users: number
    max_projects: number
    max_executions_per_month: number | null
    monthly_price_cents: number
    display_name: string | null
    yearly_price_cents: number | null
    description: string | null
    tier_category: string
    is_active: boolean
    sort_order: number
    created_at: string
    updated_at: string
}

/** subscriptions-service organization subscription */
export interface OrganizationSubscription {
    id: number
    organization_id: string
    subscription_tier: string
    status: string
    billing_cycle: string
    current_period_start?: string
    current_period_end: string
    max_users: number
    max_projects: number
    max_test_cases?: number
    max_executions_per_month?: number
    trial_ends_at?: string
    cancel_at_period_end?: boolean
    cancelled_at?: string
    stripe_subscription_id?: string
    stripe_customer_id?: string
    created_at: string
    updated_at: string
}

/** Available add-on module from /api/subscriptions/modules/available */
export interface SubscriptionModule {
    id: number
    module_key: string
    name: string
    description: string
    category: string
    monthly_price_cents: number
    yearly_price_cents: number
    usage_type: string
    usage_unit?: string
    default_monthly_quota?: number
    icon?: string
    is_active: boolean
    sort_order: number
}

/**
 * Enabled module from /api/subscriptions/modules
 * Backend returns addon_module_id and module_key.
 * Pages reference module_id, module_code, and enabled — included as aliases.
 */
export interface EnabledModule {
    id: number
    addon_module_id: number
    /** Alias for addon_module_id — used by SubscriptionsPage */
    module_id: number
    module_key: string
    /** Alias for module_key — used by AdvancedReportsPage */
    module_code: string
    name: string
    description: string
    status: string
    /** Derived: true when status === 'active' */
    enabled: boolean
    started_at: string
    expires_at?: string
}

export interface UpdateSubscriptionRequest {
    subscription_tier?: string
    billing_cycle?: string
    max_users?: number
    max_projects?: number
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Safely extract an array from an API response that may be:
 *   - a bare array: [...]
 *   - a wrapped object: { tiers: [...] }, { modules: [...] }, { items: [...] }, { data: [...] }
 * Returns [] if nothing recognizable is found.
 */
function extractArray<T>(data: unknown): T[] {
    if (Array.isArray(data)) return data
    if (data && typeof data === 'object') {
        const obj = data as Record<string, unknown>
        for (const key of ['tiers', 'modules', 'items', 'data', 'results']) {
            if (Array.isArray(obj[key])) return obj[key] as T[]
        }
        // Last resort: check all values for a single array property
        const arrays = Object.values(obj).filter(Array.isArray)
        if (arrays.length === 1) return arrays[0] as T[]
    }
    return []
}

// ============================================================================
// Query Keys
// ============================================================================

export const subscriptionsKeys = {
    all: ['subscriptions'] as const,
    availableTiers: (category?: string) =>
        [...subscriptionsKeys.all, 'tiers', { category }] as const,
    organizationSubscription: () =>
        [...subscriptionsKeys.all, 'organization'] as const,
    availableModules: () =>
        [...subscriptionsKeys.all, 'modules', 'available'] as const,
    enabledModules: () =>
        [...subscriptionsKeys.all, 'modules', 'enabled'] as const,
}

// ============================================================================
// Available Subscription Tiers
// OpenAPI: GET /api/billing/tiers?status_filter=&tier_category=&limit=&offset=
// ============================================================================

export const useAvailableTiers = (
    category?: 'standard' | 'enterprise' | 'on_prem'
) => {
    return useQuery({
        queryKey: subscriptionsKeys.availableTiers(category),
        queryFn: async () => {
            const params = new URLSearchParams()
            if (category) {
                params.append('tier_category', category)
            }
            const query = params.toString()
            const url = `/api/subscriptions/tiers${query ? `?${query}` : ''}`
            const { data } = await apiClient.get(url)
            return extractArray<SubscriptionTier>(data)
        },
    })
}

// ============================================================================
// Organization Subscription
// OpenAPI: GET /api/subscriptions/organization
//          PUT /api/subscriptions/organization
// ============================================================================

export const useOrganizationSubscription = () => {
    return useQuery({
        queryKey: subscriptionsKeys.organizationSubscription(),
        queryFn: async () => {
            const { data } = await apiClient.get<OrganizationSubscription>(
                '/api/subscriptions/organization'
            )
            return data
        },
    })
}

export const useUpdateOrganizationSubscription = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (subscriptionData: UpdateSubscriptionRequest) => {
            const { data } = await apiClient.put<OrganizationSubscription>(
                '/api/subscriptions/organization',
                subscriptionData
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: subscriptionsKeys.organizationSubscription(),
            })
        },
    })
}

// ============================================================================
// Subscription Modules
// OpenAPI: GET /api/subscriptions/modules/available
//          GET /api/subscriptions/modules
//          POST /api/subscriptions/modules/{module_id}
//          DELETE /api/subscriptions/modules/{module_id}
//          PUT /api/subscriptions/modules/{module_id}/toggle
// ============================================================================

/** Helper to normalize backend response into EnabledModule with aliases */
function normalizeEnabledModule(raw: Record<string, unknown>): EnabledModule {
    const addonModuleId = (raw.addon_module_id ?? raw.module_id ?? 0) as number
    const moduleKey = (raw.module_key ?? raw.module_code ?? '') as string
    const status = (raw.status ?? 'inactive') as string

    return {
        id: raw.id as number,
        addon_module_id: addonModuleId,
        module_id: addonModuleId,
        module_key: moduleKey,
        module_code: moduleKey,
        name: (raw.name ?? raw.module_name ?? '') as string,
        description: (raw.description ?? '') as string,
        status,
        enabled: status === 'active',
        started_at: (raw.started_at ?? raw.activated_at ?? '') as string,
        expires_at: raw.expires_at as string | undefined,
    }
}

export const useAvailableModules = () => {
    return useQuery({
        queryKey: subscriptionsKeys.availableModules(),
        queryFn: async () => {
            const { data } = await apiClient.get(
                '/api/subscriptions/modules/available'
            )
            return extractArray<SubscriptionModule>(data)
        },
    })
}

export const useEnabledModules = () => {
    return useQuery({
        queryKey: subscriptionsKeys.enabledModules(),
        queryFn: async () => {
            const { data } = await apiClient.get(
                '/api/subscriptions/modules'
            )
            const raw = extractArray<Record<string, unknown>>(data)
            return raw.map(normalizeEnabledModule)
        },
    })
}

export const useEnableModule = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (moduleId: number) => {
            const { data } = await apiClient.post(
                `/api/subscriptions/modules/${moduleId}`
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: subscriptionsKeys.enabledModules(),
            })
        },
    })
}

export const useToggleModule = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({
                               moduleId,
                               enabled,
                           }: {
            moduleId: number
            enabled: boolean
        }) => {
            const { data } = await apiClient.put(
                `/api/subscriptions/modules/${moduleId}/toggle`,
                { enabled }
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: subscriptionsKeys.enabledModules(),
            })
        },
    })
}

export const useDisableModule = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (moduleId: number) => {
            await apiClient.delete(`/api/subscriptions/modules/${moduleId}`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: subscriptionsKeys.enabledModules(),
            })
        },
    })
}

// ============================================================================
// Stripe Checkout (placeholder — Chase integration planned)
// ============================================================================

export const useCreateCheckoutSession = () => {
    return useMutation({
        mutationFn: async (params: {
            tier: string
            billing_cycle: string
        }) => {
            const { data } = await apiClient.post<{ checkout_url: string }>(
                '/api/subscriptions/stripe/checkout-session',
                params
            )
            return data
        },
    })
}